'use client';

import type { ReactNode } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { EmptyState } from './ui/EmptyState';
import { ItemGridSkeleton } from './ItemGridSkeleton';
import { Button } from './ui/Button';
import { RiArrowRightSLine } from './icons';
import { ITEM_GRID_GAP, ITEM_GRID_MIN_COLUMN } from './ItemListItem';

interface PagedItemGridProps<T> {
  items: T[];
  page: number;
  totalPages: number | null;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  renderItem: (item: T, index: number) => ReactNode;
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  errorText: string;
  minColumn?: number;
  skeletonCardClassName?: string;
}

export function PagedItemGrid<T>({
  items,
  page,
  totalPages,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  isLoading,
  isError,
  refetch,
  renderItem,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorText,
  minColumn = ITEM_GRID_MIN_COLUMN,
  skeletonCardClassName,
}: PagedItemGridProps<T>): ReactNode {
  if (isLoading && items.length === 0) {
    return <ItemGridSkeleton count={8} minColumn={minColumn} cardClassName={skeletonCardClassName} />;
  }
  if (isError) {
    return <ErrorMessage error={errorText} onRetry={refetch} />;
  }
  if (items.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid items-start pt-2"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minColumn}px, 1fr))`, gap: ITEM_GRID_GAP }}
      >
        {items.map((item, i) => renderItem(item, i))}
      </div>
      {(hasPrev || hasNext) && (
        <div className="flex items-center justify-center gap-3" data-testid="paged-grid-pager">
          <Button size="m" variant="tertiary" className="px-3" disabled={!hasPrev} onClick={onPrev} aria-label="‹">
            <RiArrowRightSLine size={18} className="-scale-x-100 rtl:scale-x-100" />
          </Button>
          <span className="min-w-[56px] text-center text-sm tabular-nums text-label-secondary">
            {totalPages ? `${page} / ${totalPages}` : page}
          </span>
          <Button size="m" variant="tertiary" className="px-3" disabled={!hasNext} onClick={onNext} aria-label="›">
            <RiArrowRightSLine size={18} className="rtl:-scale-x-100" />
          </Button>
        </div>
      )}
    </div>
  );
}
